import { FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import type { ContractDto } from '@b-stock/contract-api-client'
import type { BidPrecision } from '@b-stock/fe-core/bstock-next/hooks'
import {
  CurrencyStringCents,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'
import { Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'

import { SHOW_CONTRACT_QUANTITIES } from '@/buyer-portal/helpers/constants'

import { YourOfferCell } from '../atoms'
import type { OfferingItemRow } from '../types'
import {
  ContractEstimatedMSRP,
  ContractEstimatedQuantity,
  ContractEstimatedTotalMSRP,
  LineAmount,
} from '../values'

const ns = 'Offerings.Details.form'

const Body = styled.div`
  box-shadow: -0.125rem 0 0.25rem 0 rgba(0, 0, 0, 0.14);
`

const Header = styled.header`
  ${Typography.Overline2}

  background: ${designColors.neutral.lightGray};
  border-bottom: 0.0625rem solid ${designColors.neutral.mediumGray};
  padding: 1.1875rem 1rem;
`

const Details = styled.ul`
  color: ${designColors.neutral.black};
  font-size: 0.75rem;
  letter-spacing: 0.015rem;
  line-height: 1.5625rem;
  list-style: none;
  margin: 0;
  padding: 0.5rem 1rem;
`

const Footer = styled.footer`
  background: ${designColors.neutral.lightGray};
  padding: 0.5rem;
`

const LineAmountWrapper = styled.div`
  ${Typography.Body4}
  color: ${designColors.neutral.black};
  padding: 0 0.5rem;
`

export const OfferingFormTile = ({
  bidPrecision,
  contract,
  currency = 'USD',
  row,
  rowIndex,
}: {
  bidPrecision: BidPrecision
  contract?: ContractDto | null
  currency?: string
  row: OfferingItemRow
  rowIndex: number
}) => {
  const isContract = !!contract
  const msrpOrCogs = isContract && contract.bidUnit === 'COGS' ? 'COGS' : 'MSRP'
  return (
    <Body>
      <Header>{row.item.attributes.description}</Header>
      {SHOW_CONTRACT_QUANTITIES && isContract && (
        <Details>
          <>
            <li>
              <FormattedMessage id={`${ns}.labels.estimatedQuantity`} />
              {': '}
              <ContractEstimatedQuantity
                value={row.item.quantity}
                contract={contract}
              />
            </li>
            <li>
              <FormattedMessage
                id={`${ns}.labels.estMsrpOrCogs`}
                values={{ msrpOrCogs }}
              />
              {': '}
              <ContractEstimatedMSRP
                value={row.item.attributes.unitRetail}
                contract={contract}
                currency={currency}
              />
            </li>
            <li>
              <FormattedMessage
                id={`${ns}.labels.estTotalMsrpOrCogs`}
                values={{ msrpOrCogs }}
              />
              {': '}
              <ContractEstimatedTotalMSRP
                value={row.item.attributes.extRetail}
                currency={currency}
              />
            </li>
            {row.item.attributes.ids?.sku ? (
              <li>
                <FormattedMessage id={`${ns}.labels.upcSlashSku`} />
                {': '}
                {row.item.attributes.ids.sku}
              </li>
            ) : null}
          </>
        </Details>
      )}
      {!isContract && (
        <Details>
          <>
            {row.item.attributes.ids?.sku ? (
              <li>
                <FormattedMessage id={`${ns}.labels.sku`} />
                {': '}
                {row.item.attributes.ids.sku}
              </li>
            ) : null}
            <li>
              <FormattedMessage id={`${ns}.labels.quantity`} />
              {': '}
              <FormattedNumber value={row.item.quantity} />
            </li>
            <li>
              <FormattedMessage id={`${ns}.labels.unitRetail`} />
              {': '}
              <CurrencyStringCents
                currency={currency}
                value={row.item.attributes.unitRetail}
              />
            </li>
            <li>
              <FormattedMessage id={`${ns}.labels.extRetail`} />
              {': '}
              <CurrencyStringCents
                currency={currency}
                value={row.item.attributes.extRetail}
              />
            </li>
          </>
        </Details>
      )}
      <Footer>
        <YourOfferCell
          bidPrecision={bidPrecision}
          contract={contract}
          currency={currency}
          row={row}
          rowIndex={rowIndex}
        />
        {!isContract && (
          <LineAmountWrapper>
            <FormattedMessage
              id={`${ns}.lineAmount`}
              values={{
                x: (
                  <LineAmount
                    contract={contract}
                    currency={currency}
                    rowIndex={rowIndex}
                  />
                ),
              }}
            />
          </LineAmountWrapper>
        )}
      </Footer>
    </Body>
  )
}
